import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { validateNoSpecialChars, validateOnlyNumbers } from '../../validation/ValidationInput';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import BackIcon from '../../images/BackIcon';
import CheckIconWhite from '../../images/CheckIconWhite';
import DatePicker from 'react-datepicker';

import {
  createSchedules,
  getDetailsSchedules,
  updateSchedules,
} from '../../services/schedule.service';
import { useResponsive } from '../../hook/useResponsive';
import { useFormKeyboard } from '../../hook/useFormKeyboard';
interface ScheduleFormValues {
  scheduleName: string;
  printName: string;
  scheduleType?: number;
  validFrom: Date | null;
  validTo: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  applicableDays: string;
}
const SchedulesDetails = () => {
  const [formValues, setFormValues] = useState<ScheduleFormValues>({
    scheduleName: '',
    printName: '',
    validFrom: null,
    validTo: null,
    startTime: null,
    endTime: null,
    applicableDays: '',
  });
  const { isMobile, getWidth } = useResponsive();
  const preLanguage = localStorage.getItem('language');
  const [language, setLanguage] = useState<any>(null);
  const limits = useFieldLimits(coreFormLimits('schedule'));
  const [errors, setErrors] = useState<any>({});
  const path = window.location.pathname;
  const [searchType, setSearchType] = useState('');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [typeHighlight, setTypeHighlight] = useState(-1);
  const dropdownTypeRef = useRef<HTMLDivElement>(null);
  const listType = [
    {
      value: 1,
      name: 'Promotion',
    },
  ];
  const id: string = path.split('/').pop() || '';
  const kb = useFormKeyboard({ autoFocus: id === 'new' && !isMobile });
  const [daysOfWeek, setDaysOfWeek] = useState([
    { id: 'monday', name: 'Monday', value: 1, isSelected: false },
    { id: 'tuesday', name: 'Tuesday', value: 2, isSelected: false },
    { id: 'wednesday', name: 'Wednesday', value: 3, isSelected: false },
    { id: 'thursday', name: 'Thursday', value: 4, isSelected: false },
    { id: 'friday', name: 'Friday', value: 5, isSelected: false },
    { id: 'saturday', name: 'Saturday', value: 6, isSelected: false },
    { id: 'sunday', name: 'Sunday', value: 7, isSelected: false },
  ]);

  // Lấy ngày hiện tại (reset về 00:00:00 để so sánh chính xác)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleChange = (field: any, value: any) => {
    // Validate End Date không được ở quá khứ (giữ restriction cho validTo)
    if (field === 'validTo' && value) {
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        setErrors((prev: any) => ({
          ...prev,
          [field]: `End Date cannot be in the past`,
        }));
        return;
      }
      // Xóa error nếu hợp lệ
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Validate End Date phải sau Start Date
    if (field === 'validTo' && value && formValues.validFrom) {
      const endDate = new Date(value);
      endDate.setHours(0, 0, 0, 0);
      const startDate = new Date(formValues.validFrom);
      startDate.setHours(0, 0, 0, 0);
      if (endDate < startDate) {
        setErrors((prev: any) => ({
          ...prev,
          validTo: 'End Date must be after Start Date',
        }));
        return;
      }
    }

    // Validate End Time phải sau Start Time
    if (field === 'endTime' && value && formValues.startTime) {
      if (value <= formValues.startTime) {
        setErrors((prev: any) => ({
          ...prev,
          endTime: 'End Time must be after Start Time',
        }));
        return;
      }
      // Xóa error nếu hợp lệ
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors.endTime;
        return newErrors;
      });
    }

    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  useEffect(() => {
    if (id !== 'new') {
      fetchData(id);
    }
    const handleNewToken = () => {
      if (id !== 'new') {
        fetchData(id);
      }
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, [path]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownTypeRef.current && !dropdownTypeRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async (id: any) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailsSchedules(id);
      const data = response.data.data;
      const parseTimeToday = (timeStr: string) => {
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        const d = new Date();
        d.setHours(hours, minutes, seconds || 0, 0);
        return d;
      };
      setFormValues({
        scheduleName: data.scheduleName,
        printName: data.printName,
        scheduleType: data.scheduleType,
        validFrom: new Date(data.validFrom),
        validTo: new Date(data.validTo),
        startTime: parseTimeToday(data.startTime),
        endTime: parseTimeToday(data.endTime),
        applicableDays: data.applicableDays,
      });
      setSearchType(listType.filter((v: any) => v.value === data.scheduleType)[0]?.name);
      setDaysOfWeek(prev => {
        const selectedValues = data.applicableDays.split(',').map(Number);
        return prev.map(day => ({
          ...day,
          isSelected: selectedValues.includes(day.value),
        }));
      });
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      webLoadingFalse();
    }
  };

  const formatDate = (dateString: Date) => {
    const d = new Date(dateString);
    // Lấy yyyy-mm-dd
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTime = (dateString: Date) => {
    const d = new Date(dateString);
    // Lấy HH:mm theo giờ địa phương
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleTypeSelect = (value: any, name: any) => {
    setFormValues((prev: any) => ({
      ...prev,
      scheduleType: value,
    }));
    setSearchType(name);
    setIsTypeDropdownOpen(false);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const applicableDays = daysOfWeek
      .filter(day => day.isSelected)
      .map(day => day.value)
      .join(',');
    const newErrors: any = validateAgainstLimits({ ...formValues, applicableDays }, limits, language);
    if (!formValues.scheduleName) {
      newErrors.scheduleName = 'Schedule Name is required';
    }
    if (!formValues.scheduleType) {
      newErrors.scheduleType = 'Schedule Type is required';
    }
    if (!formValues.printName) {
      newErrors.printName = 'Print Name is required';
    }
    if (!formValues.validFrom) {
      newErrors.validFrom = 'Start Date is required';
    }
    if (!formValues.validTo) {
      newErrors.validTo = 'End Date is required';
    } else {
      const selectedDate = new Date(formValues.validTo);
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.validTo = 'End Date cannot be in the past';
      }
      // Kiểm tra End Date phải sau Start Date
      if (formValues.validFrom) {
        const startDate = new Date(formValues.validFrom);
        startDate.setHours(0, 0, 0, 0);
        if (selectedDate < startDate) {
          newErrors.validTo = 'End Date must be after Start Date';
        }
      }
    }
    if (!formValues.startTime) {
      newErrors.startTime = 'Start Time is required';
    }
    if (!formValues.endTime) {
      newErrors.endTime = 'End Time is required';
    } else if (formValues.startTime && formValues.endTime) {
      // Kiểm tra End Time phải sau Start Time
      if (formValues.endTime <= formValues.startTime) {
        newErrors.endTime = 'End Time must be after Start Time';
      }
    }
    if (applicableDays === '') {
      newErrors.applicableDays = 'Please select one of the option';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    webLoadingTrue();

    const payload: any = {
      ...formValues,
      applicableDays: applicableDays,
      validFrom: formatDate(formValues?.validFrom || new Date()),
      validTo: formatDate(formValues?.validTo || new Date()),
      startTime: formatTime(formValues?.startTime || new Date()),
      endTime: formatTime(formValues?.endTime || new Date()),
    };
    if (id === 'new') {
      try {
        const response: any = await createSchedules(payload);
        webNavigate('/' + preLanguage + '/schedules');
        webLoadingFalse();
        showToast({
          message: 'Schedules Created !',
          type: 'success',
          duration: 20000,
        });
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: setErrors });
      }
    } else {
      try {
        const response: any = await updateSchedules(id, payload);
        webNavigate('/' + preLanguage + '/schedules');
        showToast({
          message: language?.['Schedules Updated !'] ?? 'Schedules Updated !',
          type: 'success',
          duration: 20000,
        });
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: setErrors });
      } finally {
        webLoadingFalse();
      }
    }
  };
  const toggleDay = (dayId: string) => {
    setDaysOfWeek(prev =>
      prev.map(day => (day.id === dayId ? { ...day, isSelected: !day.isSelected } : day))
    );
  };
  // Click-only type list: arrows move, Enter/Tab pick the highlight, Escape closes.
  const handleTypeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const from = isTypeDropdownOpen ? typeHighlight : -1;
      setIsTypeDropdownOpen(true);
      setTypeHighlight(e.key === 'ArrowDown' ? Math.min(listType.length - 1, from + 1) : Math.max(0, from - 1));
      return;
    }
    // Ctrl/Cmd+Enter only closes the list; not prevented, so the form still saves.
    if (isTypeDropdownOpen && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      setIsTypeDropdownOpen(false);
      return;
    }
    if (isTypeDropdownOpen && (e.key === 'Enter' || e.key === 'Tab')) {
      if (e.key === 'Enter') e.preventDefault();
      const item = listType[typeHighlight];
      if (item) handleTypeSelect(item.value, item.name);
      else setIsTypeDropdownOpen(false);
      return;
    }
    if (isTypeDropdownOpen && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsTypeDropdownOpen(false);
      return;
    }
    validateNoSpecialChars(e);
  };
  return (
    <div {...kb.scopeProps}>
    <div className="bg-white h-full">
      <div className="flex items-center ">
        <button
          type="button"
          aria-label={language?.['Back'] ?? 'Back'}
          className="w-6 h-6 flex items-center justify-center cursor-pointer"
          onClick={() => {
            webNavigate('/' + preLanguage + '/schedules');
          }}
        >
          <BackIcon />
        </button>
        <p
          style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
          className="font-inter"
        >
          {language && language?.['Back'] ? language?.['Back'] : 'Back'}
        </p>
      </div>
      {id === 'new' ? (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Add schedule'] ? language?.['Add schedule'] : 'Add schedule'}
        </p>
      ) : (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Edit schedule'] ? language?.['Edit schedule'] : 'Edit schedule'}
        </p>
      )}
      <div
        className='scroll-container'
        style={{
          width: '100%',
          height: 'calc(100% - 163px)',
          paddingBottom: '16px'
        }}
      >
        <div>
          <div className="space-y-2 w-full">
            <div className="flex items-center">
              <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                {language && language?.['Schedule name']
                  ? language?.['Schedule name']
                  : 'Schedule name'}
              </p>
              <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                {language && language?.['*'] ? language?.['*'] : '*'}
              </p>
            </div>
            <div className="w-full">
              <input
                value={formValues?.scheduleName}
                onKeyDown={validateNoSpecialChars}
                name="scheduleName"
                {...limitProps(limits, 'scheduleName')}
                onChange={e => handleChange('scheduleName', e.target.value)}
                className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
              />
              <FieldCounter value={formValues?.scheduleName} max={limits.scheduleName?.maxLength} />
            </div>
            {errors.scheduleName && <p className="error">{errors.scheduleName}</p>}
          </div>
          <div className="flex items-center" style={{ gap: '24px', marginTop: '24px' }}>
            <div className="space-y-2" style={{ width: '50%' }}>
              <div className="flex items-center">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['Print name'] ? language?.['Print name'] : 'Print name'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <input
                  value={formValues?.printName}
                  onKeyDown={validateNoSpecialChars}
                  name="printName"
                  {...limitProps(limits, 'printName')}
                  onChange={e => handleChange('printName', e.target.value)}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                <FieldCounter value={formValues?.printName} max={limits.printName?.maxLength} />
              </div>
              {errors.printName && <p className="error">{errors.printName}</p>}
            </div>
            <div className="space-y-2" style={{ width: '50%' }}>
              <div className="flex items-center">
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#4A5353' }}
                >
                  {language && language?.['Schedule Type']
                    ? language?.['Schedule Type']
                    : 'Schedule Type'}
                </p>
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: '#BA0E13' }}
                >
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="relative" ref={dropdownTypeRef}>
                <input
                  type="text"
                  value={searchType}
                  role="combobox"
                  aria-expanded={isTypeDropdownOpen}
                  aria-autocomplete="list"
                  onChange={e => {
                    setSearchType(e.target.value);
                    setIsTypeDropdownOpen(true);
                    setTypeHighlight(-1);
                  }}
                  onClick={() => {
                    setIsTypeDropdownOpen(true);
                    setTypeHighlight(-1);
                  }}
                  onKeyDown={handleTypeKeyDown}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                {errors.type && <p className="error">{errors.type}</p>}
                {isTypeDropdownOpen && (
                  <div role="listbox" className="absolute z-10 w-full mt-1 bg-white border border-[#EBEBED] rounded-md shadow-lg max-h-60 overflow-auto">
                    {listType.map((item: any, index: number) => (
                      <div
                        key={item.value}
                        role="option"
                        aria-selected={index === typeHighlight}
                        className="px-4 py-2 cursor-pointer hover:bg-[#E7F1F1] text-main"
                        style={index === typeHighlight ? { backgroundColor: '#E7F1F1' } : undefined}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => handleTypeSelect(item.value, item.name)}
                      >
                        {item.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {errors.scheduleType && <p className="error">{errors.scheduleType}</p>}
          </div>
          <div className="flex items-center" style={{ gap: '24px', marginTop: '24px' }}>
            <div className="space-y-2 w-full" style={{ width: '50%' }}>
              <div className="flex items-center">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['Start date'] ? language?.['Start date'] : 'Start date'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <DatePicker
                  selected={formValues.validFrom}
                  onChange={date => handleChange('validFrom', date)}
                  dateFormat="dd.MM.yyyy"
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                {errors.validFrom && <p className="error">{errors.validFrom}</p>}
              </div>
            </div>
            <div className="space-y-2 w-full" style={{ width: '50%' }}>
              <div className="flex items-center">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['End date'] ? language?.['End date'] : 'End date'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <DatePicker
                  selected={formValues.validTo}
                  onChange={date => handleChange('validTo', date)}
                  dateFormat="dd.MM.yyyy"
                  minDate={formValues.validFrom || today}
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                {errors.validTo && <p className="error">{errors.validTo}</p>}
              </div>
            </div>
          </div>

          <div className="flex items-center" style={{ gap: '24px', marginTop: '24px' }}>
            <div className="space-y-2 w-full" style={{ width: '50%' }}>
              <div className="flex items-center">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['Start time'] ? language?.['Start time'] : 'Start time'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <DatePicker
                  selected={formValues.startTime}
                  onChange={date => handleChange('startTime', date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                {errors.startTime && <p className="error">{errors.startTime}</p>}
              </div>
            </div>
            <div className="space-y-2 w-full" style={{ width: '50%' }}>
              <div className="flex items-center">
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
                  {language && language?.['End time'] ? language?.['End time'] : 'End time'}
                </p>
                <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                  {language && language?.['*'] ? language?.['*'] : '*'}
                </p>
              </div>
              <div className="w-full">
                <DatePicker
                  selected={formValues.endTime}
                  onChange={date => handleChange('endTime', date)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  className={`text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                />
                {errors.endTime && <p className="error">{errors.endTime}</p>}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            width: '100%',
            marginTop: '24px',
          }}
        >
          <div className=" flex items-center">
            <p
              style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#4A5353' }}
              className="font-inter"
            >
              {language?.['Select days'] ?? 'Select days'}
            </p>
            <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
              {language && language?.['*'] ? language?.['*'] : '*'}
            </p>
          </div>
          <div className="space-y-1">
            {daysOfWeek.map(day => {
              return (
                <div
                  key={day.id}
                  style={{ gap: '8px', marginTop: '16px' }}
                  className="flex items-center rounded-md transition-colors"
                >
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '6px',
                      backgroundColor: day.isSelected ? '#0F7679' : 'white',
                      border: day.isSelected ? 'none' : '2px solid #d1d5db',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleDay(day.id)}
                    onKeyDown={e => {
                      // Space toggles, as on a native checkbox; Enter is left to the form.
                      if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey) return;
                      e.preventDefault();
                      if (!e.repeat) toggleDay(day.id);
                    }}
                    role="checkbox"
                    aria-checked={day.isSelected}
                    aria-label={day.name}
                    tabIndex={0}
                  >
                    {day.isSelected && <CheckIconWhite />}
                  </div>
                  <span
                    className="text-sm font-medium text-gray-700"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleDay(day.id)}
                  >
                    {day.name}
                  </span>
                </div>
              );
            })}
            {errors.applicableDays && <p className="error">{errors.applicableDays}</p>}
          </div>
        </div>
      </div>
      <div
        className="w-full flex items-center justify-end shadow_button relative"
        style={{ gap: "12px", paddingTop: "24px", }}
      >
        <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
        <button
          type="button"
          onClick={() => {
            webNavigate('/' + preLanguage + '/schedules');
          }}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#222525',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #C9D7D8',
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
        </button>
        <button
          type="button"
          {...kb.primaryProps(handleSubmit)}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: '#0F7679',
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.['Save'] ? language?.['Save'] : 'Save'}
        </button>
      </div>
    </div>
    </div>
  );
};
export default SchedulesDetails;
